import { useNavSections } from '@/components/sidebar/nav-config';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from '@/components/ui/command';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface CommandPaletteProps {
  /**
   * Allow callers to control open state (used by tests). Omitted in
   * normal usage — the palette manages its own state and reacts to the
   * Cmd/Ctrl+K shortcut.
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * Global Cmd/Ctrl+K command palette. Items come from the sidebar
 * nav-config — every nav destination becomes a palette command, with the
 * same permission gating (so a viewer-only principal doesn't see admin
 * commands they couldn't execute). G2-T11 ships with this scope;
 * G3-T13 later adds plugin and team search once the catalog moves to
 * `/api/plugins` (open question #1 — viewer accessibility is the
 * blocker).
 *
 * The shortcut listens at the window level so the palette opens from
 * anywhere — chrome, page content, focused inputs — and uses Radix's
 * dialog primitive (via CommandDialog) for focus-trap and escape
 * handling.
 */
export function CommandPalette({ open: controlledOpen, onOpenChange }: CommandPaletteProps = {}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;

  const setOpen = useCallback(
    (next: boolean) => {
      setInternalOpen(next);
      onOpenChange?.(next);
    },
    [onOpenChange],
  );

  const navigate = useNavigate();
  const sections = useNavSections();

  // Mirror `open` into a ref so the global keydown handler reads the
  // current value without re-binding on every toggle. The listener is
  // bound once for the component's lifetime.
  const openRef = useRef(open);
  openRef.current = open;

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'k' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setOpen(!openRef.current);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setOpen]);

  const handleSelect = (to: string) => {
    setOpen(false);
    // Hash-style admin links (`/admin#flags`) — react-router v7 handles
    // these as full navigations; the AdminDashboardPage's hash watcher
    // flips the visible tab on the hashchange event.
    void navigate(to);
  };

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Search pages and actions..." />
      <CommandList>
        <CommandEmpty>No results.</CommandEmpty>
        {sections.map((section, index) => (
          <div key={section.label ?? `section-${String(index)}`}>
            {index > 0 ? <CommandSeparator /> : null}
            <CommandGroup heading={section.label ?? 'Navigate'}>
              {section.items.map((item) => (
                <CommandItem
                  key={item.to}
                  value={`${section.label ?? ''} ${item.label}`}
                  onSelect={() => handleSelect(item.to)}
                >
                  {item.Icon !== undefined ? (
                    <item.Icon className="mr-2 h-4 w-4" aria-hidden="true" />
                  ) : null}
                  <span>{item.label}</span>
                  <CommandShortcut>↵</CommandShortcut>
                </CommandItem>
              ))}
            </CommandGroup>
          </div>
        ))}
      </CommandList>
    </CommandDialog>
  );
}
